import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import MediaCard from '@/components/base/MediaCard';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import {
  genreOptions,
  mediaList,
  quickTags,
  regionOptions,
  yearOptions,
} from '@/mocks/movies';

const PAGE_SIZE = 8;

type SortKey = 'updated' | 'heat' | 'rating';
type ScoreKey = 'all' | '9' | '85' | '8';

const scoreOptions: { key: ScoreKey; label: string }[] = [
  { key: 'all', label: '全部评分' },
  { key: '9', label: '9.0 以上' },
  { key: '85', label: '8.5 以上' },
  { key: '8', label: '8.0 以上' },
];

const typeOptions = [
  { key: 'all', label: '全部' },
  { key: 'tv', label: '电视剧' },
  { key: 'movie', label: '电影' },
];

const sortOptions: { key: SortKey; label: string }[] = [
  { key: 'updated', label: '按更新时间' },
  { key: 'heat', label: '按热度' },
  { key: 'rating', label: '按评分' },
];

export default function CategoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [type, setType] = useState(searchParams.get('type') ?? 'all');
  const [region, setRegion] = useState('全部地区');
  const [year, setYear] = useState('全部年份');
  const [genre, setGenre] = useState('全部类型');
  const [score, setScore] = useState<ScoreKey>('all');
  const [tag, setTag] = useState(searchParams.get('tag') ?? '');
  const [sort, setSort] = useState<SortKey>('updated');
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    setType(searchParams.get('type') ?? 'all');
    setTag(searchParams.get('tag') ?? '');
    setPage(1);
  }, [searchParams]);

  const pageTitle =
    type === 'tv' ? '电视剧分类' : type === 'movie' ? '电影分类' : '影视分类大全';

  useDocumentMeta({
    title: `${pageTitle} - 粉色水蜜桃影视大全`,
    description:
      '粉色水蜜桃提供丰富的电视剧、电影分类资源，支持按类型、地区、年份与评分筛选，助您快速找到喜欢的影视剧。',
    keywords: '粉色水蜜桃,影视分类,电视剧分类,电影分类,影视大全,剧情介绍',
  });

  const filtered = useMemo(() => {
    const list = mediaList.filter((item) => {
      if (type !== 'all' && item.type !== type) return false;
      if (region !== '全部地区' && item.region !== region) return false;
      if (year !== '全部年份' && String(item.year) !== year) return false;
      if (genre !== '全部类型' && !item.genres.includes(genre)) return false;
      if (tag && !item.tags.includes(tag)) return false;
      if (score === '9' && item.rating < 9) return false;
      if (score === '85' && item.rating < 8.5) return false;
      if (score === '8' && item.rating < 8) return false;
      return true;
    });

    list.sort((a, b) => {
      if (sort === 'heat') return b.heat - a.heat;
      if (sort === 'rating') return b.rating - a.rating;
      return b.updatedAt.localeCompare(a.updatedAt);
    });

    return list;
  }, [type, region, year, genre, tag, score, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const resetFilters = () => {
    setType('all');
    setRegion('全部地区');
    setYear('全部年份');
    setGenre('全部类型');
    setScore('all');
    setTag('');
    setPage(1);
    setSearchParams({});
  };

  const handleFilterChange = (fn: () => void) => {
    fn();
    setPage(1);
  };

  const goPage = (next: number) => {
    setPage(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const selectClass =
    'h-10 rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400 cursor-pointer';

  return (
    <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
      <nav className="flex items-center gap-2 text-sm text-foreground-500" aria-label="面包屑">
        <Link to="/" className="transition-colors hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
        <span className="text-foreground-800">影视分类</span>
      </nav>

      <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            {pageTitle}
          </h1>
          <p className="mt-2 text-sm text-foreground-600">
            共收录 <span className="font-semibold text-primary-600">{filtered.length}</span>{' '}
            部作品，支持按类型、地区、年份与评分筛选
          </p>
        </div>
        <div className="hidden items-center gap-2 md:flex">
          <span className="text-sm text-foreground-500">排序</span>
          <div className="inline-flex rounded-full border border-background-200 bg-background-100 p-1">
            {sortOptions.map((option) => (
              <button
                key={option.key}
                type="button"
                onClick={() => handleFilterChange(() => setSort(option.key))}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors cursor-pointer ${
                  sort === option.key
                    ? 'bg-background-50 text-primary-600'
                    : 'text-foreground-600 hover:text-primary-600'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-6 rounded-lg border border-background-200 bg-background-50">
        <div className="flex items-center justify-between gap-3 border-b border-background-200 p-4 md:hidden">
          <span className="text-sm font-medium text-foreground-800">筛选条件</span>
          <button
            type="button"
            onClick={() => setShowFilters((prev) => !prev)}
            className="flex items-center gap-1 rounded-full border border-background-300 px-3 py-1.5 text-sm text-foreground-700 cursor-pointer"
          >
            <i className={showFilters ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} aria-hidden="true"></i>
            {showFilters ? '收起' : '展开'}
          </button>
        </div>

        <div className={`${showFilters ? 'block' : 'hidden'} p-4 md:block`}>
          <div className="flex flex-wrap items-center gap-3">
            <span className="w-12 shrink-0 text-sm text-foreground-500">类型</span>
            <div className="inline-flex rounded-full border border-background-200 bg-background-100 p-1">
              {typeOptions.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => handleFilterChange(() => setType(option.key))}
                  className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors cursor-pointer ${
                    type === option.key
                      ? 'bg-background-50 text-primary-600'
                      : 'text-foreground-600 hover:text-primary-600'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <span className="w-12 shrink-0 text-sm text-foreground-500">筛选</span>
            <select
              value={region}
              onChange={(e) => handleFilterChange(() => setRegion(e.target.value))}
              className={selectClass}
              aria-label="地区"
            >
              {regionOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <select
              value={year}
              onChange={(e) => handleFilterChange(() => setYear(e.target.value))}
              className={selectClass}
              aria-label="年份"
            >
              {yearOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <select
              value={genre}
              onChange={(e) => handleFilterChange(() => setGenre(e.target.value))}
              className={selectClass}
              aria-label="题材"
            >
              {genreOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <select
              value={score}
              onChange={(e) => handleFilterChange(() => setScore(e.target.value as ScoreKey))}
              className={selectClass}
              aria-label="评分"
            >
              {scoreOptions.map((option) => (
                <option key={option.key} value={option.key}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <span className="w-12 shrink-0 text-sm text-foreground-500">标签</span>
            <div className="flex flex-wrap gap-2">
              {quickTags.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => handleFilterChange(() => setTag(tag === item ? '' : item))}
                  className={`rounded-full border px-3.5 py-1.5 text-sm transition-colors cursor-pointer ${
                    tag === item
                      ? 'border-primary-400 bg-primary-50 text-primary-700'
                      : 'border-background-300 text-foreground-700 hover:border-primary-300'
                  }`}
                >
                  {item}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between gap-3 border-t border-background-200 pt-4">
            <div className="inline-flex rounded-full border border-background-200 bg-background-100 p-1 md:hidden">
              {sortOptions.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => handleFilterChange(() => setSort(option.key))}
                  className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors cursor-pointer ${
                    sort === option.key
                      ? 'bg-background-50 text-primary-600'
                      : 'text-foreground-600'
                  }`}
                >
                  {option.label.replace('按', '')}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={resetFilters}
              className="flex items-center gap-1 text-sm text-foreground-600 transition-colors hover:text-primary-600 cursor-pointer md:ml-auto"
            >
              <i className="ri-refresh-line" aria-hidden="true"></i>
              重置筛选
            </button>
          </div>
        </div>
      </div>

      {paged.length > 0 ? (
        <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 md:gap-5">
          {paged.map((item) => (
            <MediaCard key={item.id} item={item} />
          ))}
        </div>
      ) : (
        <div className="mt-8 flex flex-col items-center justify-center rounded-lg border border-background-200 bg-background-50 py-16 text-center">
          <i className="ri-inbox-2-line text-4xl text-foreground-400" aria-hidden="true"></i>
          <p className="mt-3 text-base font-medium text-foreground-800">暂无匹配的影视作品</p>
          <p className="mt-1 text-sm text-foreground-500">试试更换筛选条件，或重置全部筛选</p>
          <button
            type="button"
            onClick={resetFilters}
            className="mt-5 whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer dark:text-foreground-950"
          >
            重置筛选
          </button>
        </div>
      )}

      {totalPages > 1 ? (
        <div className="mt-10 flex items-center justify-center gap-2">
          <button
            type="button"
            onClick={() => goPage(currentPage - 1)}
            disabled={currentPage === 1}
            className="flex h-9 items-center gap-1 rounded-full border border-background-300 px-4 text-sm text-foreground-700 transition-colors hover:border-primary-300 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
          >
            <i className="ri-arrow-left-s-line" aria-hidden="true"></i>
            上一页
          </button>
          {Array.from({ length: totalPages }).map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => goPage(i + 1)}
              className={`h-9 w-9 rounded-full text-sm font-medium transition-colors cursor-pointer ${
                currentPage === i + 1
                  ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                  : 'border border-background-300 text-foreground-700 hover:border-primary-300'
              }`}
            >
              {i + 1}
            </button>
          ))}
          <button
            type="button"
            onClick={() => goPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="flex h-9 items-center gap-1 rounded-full border border-background-300 px-4 text-sm text-foreground-700 transition-colors hover:border-primary-300 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
          >
            下一页
            <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
          </button>
        </div>
      ) : null}
    </div>
  );
}